import { useState, useEffect, useMemo } from "react";
import ChangePeriodRow from "../../../components/common/ChangePeriodRow";
import {
	useCashflowQuery,
	useTransactionsQuery,
	useCategoriesQuery,
} from "../../../services/queryHooks";
import { Loading } from "../../../components/common/Loading";
import CashFlowChart from "./CashflowChart";
import PeriodSummary from "./PeriodSummary";
import CategoriesPieChart from "./CategoriesPieChart";
import Transactions from "../../Transactions/Transactions";
import { Box } from "@mui/material";

function capitalizeFirstLetter(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

function returnPeriodName(date, periodType, userLanguage) {
	const dateOptions = {
		year: "numeric",
		...(periodType === "Monthly" && { month: "long" }),
	};
	return capitalizeFirstLetter(
		date.toLocaleDateString(userLanguage, dateOptions)
	);
}

function Cashflow() {
	const [period, setPeriod] = useState(() => {
		const d = new Date();
		d.setUTCHours(0, 0, 0, 0);
		return d.toISOString();
	});
	const [periodType, setPeriodType] = useState("Monthly");
	const [clickedItem, setClickedItem] = useState(null);
	const [selectedGroup, setSelectedGroup] = useState(null);

	const { data: cashflowData, isLoading: isLoadingCashflow } =
		useCashflowQuery(period, periodType);
	const { data: categoriesData, isLoading: isLoadingCategories } =
		useCategoriesQuery();
	const { data: transactionsData, isLoading: isLoadingTransactions } =
		useTransactionsQuery(
			clickedItem?.startDate,
			clickedItem?.endDate,
			clickedItem?.type,
			true,
			!!clickedItem
		);
	const userLanguage = localStorage.getItem("i18nextLng") || "en";

	useEffect(() => {
		const date = new Date(period);
		let startDate, endDate;

		if (periodType === "Monthly") {
			const year = date.getUTCFullYear();
			const month = date.getUTCMonth();

			startDate = new Date(Date.UTC(year, month, 1));
			endDate = new Date(Date.UTC(year, month + 1, 0));
		} else if (periodType === "Yearly") {
			const year = date.getUTCFullYear();

			startDate = new Date(Date.UTC(year, 0, 1));
			endDate = new Date(Date.UTC(year, 11, 31));
		}

		setClickedItem({
			type: null,
			startDate,
			endDate,
		});

		setSelectedGroup(null);
	}, [period, periodType]);

	useEffect(() => {
		if (!clickedItem) {
			const now = new Date();
			const year = now.getFullYear();
			const month = now.getMonth() + 1;
			const startDate = new Date(year, month - 1, 1);
			const endDate = new Date(year, month, 0);

			setClickedItem({
				type: null,
				startDate,
				endDate,
			});
			setSelectedGroup(null);
		}
	}, [clickedItem]);

	const filteredTransactions = useMemo(() => {
		if (!transactionsData) return [];
		if (!selectedGroup) return transactionsData;

		return transactionsData.filter(
			(t) => t.categoryId === selectedGroup._id
		);
	}, [transactionsData, selectedGroup]);

	if (isLoadingCashflow || isLoadingCategories) {
		return <Loading />;
	}

	return (
		<>
			<ChangePeriodRow
				period={period}
				setPeriod={setPeriod}
				periodType={periodType}
				setPeriodType={setPeriodType}
				periodTypeOptions={["Monthly", "Yearly"]}
			/>
			{cashflowData && <PeriodSummary data={cashflowData} />}
			<CashFlowChart
				data={cashflowData}
				setClickedItem={setClickedItem}
				setSelectedGroup={setSelectedGroup}
			/>

			{clickedItem && (
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						flexWrap: "wrap",
						gap: 2,
						m: 2,
					}}
				>
					<CategoriesPieChart
						clickedItem={clickedItem}
						setClickedItem={setClickedItem}
						transactionsData={filteredTransactions}
						categoriesData={categoriesData}
						clickedPeriodName={
							clickedItem?.startDate
								? returnPeriodName(
										clickedItem.startDate,
										periodType,
										userLanguage
									)
								: ""
						}
						selectedGroup={selectedGroup}
						setSelectedGroup={setSelectedGroup}
					/>
					<Transactions
						customTransactions={filteredTransactions}
						customTransactionsLoading={isLoadingTransactions}
						simplifiedTable={true}
					/>
				</Box>
			)}
		</>
	);
}

export default Cashflow;
